/*
  See @klarna/bubble-ui documentation on how to use colors
  Kleco specific docs can be found in https://kleco.klarna.net/docs/ui/colors/
*/
import { ParticleDesignVersions, constants } from '@klarna/bubble-ui'

import { path } from 'ramda'

export const HPP_LIGHT = '#f2f2f1'
export const HPP_DARK = '#0E0E0F'
export const HPP_DARK_MOBILE = '#171717'

export const DESIGN_VERSIONS = {
  DARK: ParticleDesignVersions.DARK as string,
  LIGHT: ParticleDesignVersions.NEXT as string
} as const

export const DARK = 'DARK'
export const LIGHT = 'LIGHT'

export const MOBILE = 'mobile'
export const TABLET = 'tablet'
export const DESKTOP = 'desktop'
export const WEB = 'web'
export const NATIVE = 'native'

export const RESERVED_PROPERTIES = [MOBILE, TABLET, DESKTOP, WEB, NATIVE]

export const DEFAULT_GUTTER = {
  mobile: 20,
  tablet: 20,
  desktop: 30
} as const

export const BREAKPOINTS = {
  [MOBILE]: 767,
  [TABLET]: 1119
}
export default class DynamicValue<T = string> {
  public DARK: T
  public LIGHT: T
  public name: string | undefined

  constructor(light: T, dark: T, options?: { name: string }) {
    this.LIGHT = light
    this.DARK = dark
    this.name = path(['name'], options)
  }

  public get(isDark = false) {
    if (isDark) {
      return this.DARK
    }

    return this.LIGHT
  }
}

export const DV_LIGHT = DESIGN_VERSIONS[LIGHT]
export const DV_DARK = DESIGN_VERSIONS[DARK]

const getColorObject = (name: string, mode: string) =>
  (constants.ColorFunctions[name] && constants.ColorFunctions[name](mode)) || {}
const getColor = (name: string, mode: string) => getColorObject(name, mode).toString()
const createDynamicValue = (name: string) =>
  new DynamicValue(getColor(name, DV_LIGHT), getColor(name, DV_DARK), { name })

export const BACKGROUND_DEFAULT = createDynamicValue('__backgroundDefault')
export const BACKGROUND_ERROR_FLOATING = createDynamicValue('__backgroundErrorFloating')
export const BACKGROUND_ERROR_INLINE = createDynamicValue('__backgroundErrorInline')
export const BACKGROUND_INVERSE = createDynamicValue('__backgroundInverse')
export const BACKGROUND_INVERSE_PRESSED = createDynamicValue('__backgroundInversePressed')
export const BACKGROUND_SUBTLE_FLOATING = createDynamicValue('__backgroundSubtleFloating')
export const BACKGROUND_SUBTLE_INLINE = createDynamicValue('__backgroundSubtleInline')
export const BACKGROUND_SUBTLE_INLINE_PRESSED = createDynamicValue(
  '__backgroundSubtleInlinePressed'
)
export const BACKGROUND_DISABLED = createDynamicValue('__backgroundDisabled')
export const BACKGROUND_DISABLED_WITH_BORDER = createDynamicValue('__backgroundDisabledWithBorder')
export const ERROR = createDynamicValue('error')
export const ERROR_BACKGROUND_REGULAR = createDynamicValue('errorBackgroundRegular')
export const ERROR_BACKGROUND_DARK = createDynamicValue('errorBackgroundDark')
export const BACKGROUND_SUCCESS_FLOATING = createDynamicValue('__backgroundSuccessFloating')
export const BACKGROUND_SUCCESS_INLINE = createDynamicValue('__backgroundSuccessInline')
export const BACKGROUND_WARNING_FLOATING = createDynamicValue('__backgroundWarningFloating')
export const BACKGROUND_WARNING_INLINE = createDynamicValue('__backgroundWarningInline')
export const BORDER_NEUTRAL = createDynamicValue('__borderNeutral')
export const BORDER_DEFAULT = createDynamicValue('__borderDefault')
export const BORDER_SUBTLE_FLOATING = createDynamicValue('__borderSubtleFloating') // dividerStrong
export const BORDER_SUBTLE_INLINE = createDynamicValue('__borderSubtleInline') // divider
export const DIVIDER_STRONG = createDynamicValue('dividerStrong')
export const BACKGROUND_ACCENT = createDynamicValue('__backgroundAccent')
export const ICON_NEUTRAL = createDynamicValue('__iconNeutral')
export const ICON_WARNING = createDynamicValue('__iconWarning')
export const ICON_ACCENT = createDynamicValue('__iconAccent')
export const ICON_ERROR = createDynamicValue('__iconError')
export const ICON_SUCCESS = createDynamicValue('__iconSuccess')
export const ICON_NEUTRAL_THICK_FONT = createDynamicValue('__iconNeutralThickFont')
export const TEXT_DEFAULT = createDynamicValue('__textDefault')
export const TEXT_ACCENT = createDynamicValue('__textAccent')
export const TEXT_INVERSE = createDynamicValue('__textInverse')
export const TEXT_INVERSE_ON_BACKGROUND = createDynamicValue('__textInverseOnBackground')
export const TEXT_NEUTRAL = createDynamicValue('__textNeutral')
export const TEXT_NEUTRAL_ON_BACKGROUND = createDynamicValue('__textNeutralOnBackground')
export const TEXT_ERROR = createDynamicValue('__textError')
export const TEXT_SUCCESS = createDynamicValue('__textSuccess')
export const TEXT_WARNING = createDynamicValue('__textWarning')
export const DATA_COLORED_ONE = createDynamicValue('__dataColoredOne')
export const DATA_COLORED_TWO = createDynamicValue('__dataColoredTwo')
export const DATA_COLORED_THREE = createDynamicValue('__dataColoredThree')
export const DATA_COLORED_FOUR = createDynamicValue('__dataColoredFour')
export const DATA_COLORED_FIVE = createDynamicValue('__dataColoredFive')
export const DATA_COLORED_SIX = createDynamicValue('__dataColoredSix')
export const DATA_COLORED_SEVEN = createDynamicValue('__dataColoredSeven')
export const DATA_COLORED_EIGHT = createDynamicValue('__dataColoredEight')
export const DATA_COLORED_NINE = createDynamicValue('__dataColoredNine')
export const DATA_COLORED_TEN = createDynamicValue('__dataColoredTen')
export const BLACK = createDynamicValue('black')
export const BLACK_DISABLED = createDynamicValue('blackDisabled')
export const BLACK_HOVERED = createDynamicValue('blackHovered')
export const BLACK_OVERLAY = createDynamicValue('blackOverlay')
export const BLACK_PRESSED = createDynamicValue('blackPressed')
export const DARK_GRAY = createDynamicValue('darkGray')
export const DARK_GRAY_PRESSED = createDynamicValue('darkGrayPressed')
export const GRAY = createDynamicValue('gray')
export const GRAY_HOVERED = createDynamicValue('grayHovered')
export const GRAY_PRESSED = createDynamicValue('grayPressed')
export const METADATA = createDynamicValue('metadata')
export const PRIMARY = createDynamicValue('primary')
export const SECONDARY = createDynamicValue('secondary')
export const SECONDARY_FOCUS = createDynamicValue('secondaryFocus')
export const TRANSPARENT = createDynamicValue('transparent')
export const WHITE = createDynamicValue('white')
export const WHITE_OVERLAY = createDynamicValue('whiteOverlay')
export const WHITE_SHADOW = createDynamicValue('whiteShadow')
export const REAL_BLACK = createDynamicValue('realBlack')
export const BLACK_SHADOW = createDynamicValue('blackShadow')
export const BLACK60 = createDynamicValue('black60')
export const SHADOW_SMALL = createDynamicValue('__shadowSmall')
export const BRAND_COLOR_102 = createDynamicValue('brandColor102')
export const BRAND_COLOR_106 = createDynamicValue('brandColor106')
export const BRAND_COLOR_106_HOVERED = createDynamicValue('brandColor106Hovered')
export const BRAND_COLOR_106_PRESSED = createDynamicValue('brandColor106Pressed')
export const BRAND_COLOR_203 = createDynamicValue('brandColor203')
export const BRAND_COLOR_204 = createDynamicValue('brandColor204')
export const BRAND_COLOR_206 = createDynamicValue('brandColor206')
export const BRAND_COLOR_302 = createDynamicValue('brandColor302')
export const BRAND_COLOR_303 = createDynamicValue('brandColor303')
export const BRAND_COLOR_306 = createDynamicValue('brandColor306')
export const BRAND_COLOR_402 = createDynamicValue('brandColor402')
export const BRAND_COLOR_403 = createDynamicValue('brandColor403')
export const BRAND_COLOR_404 = createDynamicValue('brandColor404')
export const BRAND_COLOR_406 = createDynamicValue('brandColor406')
export const BRAND_COLOR_502 = createDynamicValue('brandColor502')
export const BRAND_COLOR_503 = createDynamicValue('brandColor503')
export const BRAND_COLOR_504 = createDynamicValue('brandColor504')
export const BRAND_COLOR_506 = createDynamicValue('brandColor506')
export const BRAND_COLOR_602 = createDynamicValue('brandColor602')
export const BRAND_COLOR_603 = createDynamicValue('brandColor603')
export const BRAND_COLOR_606 = createDynamicValue('brandColor606')
export const BRAND_COLOR_702 = createDynamicValue('brandColor702')
export const BRAND_COLOR_706 = createDynamicValue('brandColor706')
export const GREEN = createDynamicValue('green')
export const RED = createDynamicValue('red')
export const FOCUS = createDynamicValue('focus')
export const SUCCESS_DIVIDER_AND_ICON = createDynamicValue('successDividerAndIcon')
export const WARNING_DIVIDER_AND_ICON = createDynamicValue('warningDividerAndIcon')
export const WARNING = createDynamicValue('warning')
export const SUCCESS_BACKGROUND_REGULAR = createDynamicValue('successBackgroundRegular')
export const ERROR_PRESSED = createDynamicValue('errorPressed')
export const DATE_PICKER = createDynamicValue('datepicker')
export const BACKGROUND_NEUTRAL = createDynamicValue('__backgroundNeutral')

// Custom colors
export const ICON_INVERSE = new DynamicValue('white', 'black') // needs to be white/black and no hex
export const ICON_DEFAULT = new DynamicValue('black', 'white') // needs to be string values not hex or rbg

export const SECONDARY40 = new DynamicValue(
  getColorObject('secondary', DV_LIGHT)
    .toHEX()
    .concat('40'),
  getColorObject('secondary', DV_DARK)
    .toHEX()
    .concat('40')
)

// Palette colors from bubble.constants.Palette
// Palette has no specific color for dark/light
// So this is not a DynamicValue
export const PALETTE_SLATE = constants.Palette.Slate.toString()
export const PALETTE_DIJON = constants.Palette.Dijon.toString()
export const PALETTE_OPAL = constants.Palette.Opal.toString()
export const PALETTE_ALMOND = constants.Palette.Almond.toString()
export const PALETTE_LOLLIPOP_MINT = constants.Palette.LollipopMint.toString()
export const PALETTE_LOLLIPOP_SMOOOTH_GREEN_LIGHT = constants.Palette.LollipopSmooothGreenLight.toString()
export const PALETTE_LOLLIPOP_POOL = constants.Palette.LollipopPool.toString()
